/*
 * Copyright (c) 2024-2025. The content provided herein is protected by copyright law. Unauthorized use, reproduction, or distribution of this material without proper licensing, knowledge, and contract agreement from the author is strictly prohibited. Any infringement of these rights may result in legal action. For inquiries regarding the use of this content, please contact the author for proper authorization and agreement.
 */

import React from "react";
import {useTranslation} from "react-i18next";
import {usePackageItemKartingOptions} from "utils/packageUtils";
import {currencyNumberFormat} from "utils/utility";
import {PackageCategory} from "types/enums/PackageCategory";
import clsx from "clsx";

interface BookingPackagesViewFooterPricesProps {
    /**
     * "detailed" — Family / Events look (may show a discount block).
     * "basic" — Basic credits look (slightly different totals styling, no discount).
     */
    variant?: "detailed" | "basic";
    /** Alt text for the discount badge image. */
    discountImageAlt?: string;
}

/**
 * Sub-component for BookingPackagesViewFooter that handles price display.
 * Uses usePackageItemKartingOptions hook to get all price data.
 */
const BookingPackagesViewFooterPrices = ({variant = "detailed", discountImageAlt = "tickets discount"}: BookingPackagesViewFooterPricesProps) => {
    const {t} = useTranslation();
    const {getPackageItemsPrice, getCreditItemsPrice, getCompetitionsPrice, getExtrasPrice, getTotalPrice, getDiscountPrice, filter} =
        usePackageItemKartingOptions();

    const ticketsPrice = getPackageItemsPrice();
    const creditsPrice = getCreditItemsPrice();
    const competitionsPrice = getCompetitionsPrice();
    const extrasPrice = getExtrasPrice();
    const totalPrice = getTotalPrice();
    const discountPrice = getDiscountPrice();

    const isFamilyCategory = filter.category === PackageCategory.Action;

    return (
        <div
            className={clsx(
                "order-1 lg:order-2 w-full lg:w-auto grow flex flex-col lg:flex-row items-start lg:justify-start",
                "gap-4 lg:gap-2 xl:gap-4 border-b lg:border-0 border-gray-100 pb-4 lg:pb-0 lg:pl-8"
            )}
        >
            <div className={"flex flex-col mx-auto md:flex-row md:flex-wrap items-center justify-center gap-x-4 gap-y-2 lg:gap-x-2 xl:gap-x-4"}>
                {/* Tickets */}
                <PriceItem icon="/img/credits/tickets-icon.svg" label={t("Tickets")} value={ticketsPrice} />

                <Separator icon="+" />

                {/* Credits */}
                <PriceItem icon="/img/credits/arbo-credits-icon.svg" label={t("Credits")} value={creditsPrice} />

                {competitionsPrice > 0 && (
                    <>
                        <Separator icon="+" />
                        <PriceItem icon="/img/competitions/competitions-trophy.svg" label={t("Competitions")} value={competitionsPrice} />
                    </>
                )}

                {extrasPrice > 0 && (
                    <>
                        <Separator icon="+" />
                        <PriceItem icon="/img/credits/credits-extras.svg" label={t("Extras")} value={extrasPrice} />
                    </>
                )}
                <Separator icon="=" />
                <PriceItem icon={null} label={t("Total amount")} value={totalPrice} />
                {discountPrice > 0 && isFamilyCategory && (
                    <div className={"flex items-center gap-1 rounded-full lg:bg-transparent"}>
                        <img src="/img/credits/credits-badge-green.svg" alt={discountImageAlt} className="size-[16px] lg:size-[20px]" />
                        <div className={"text-xs  text-black whitespace-nowrap"}>
                            {t("Saves")}: <span>{currencyNumberFormat(discountPrice)}</span>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

interface PriceItemProps {
    icon: string | null;
    label: string;
    value: number;
}

const PriceItem = ({icon, label, value}: PriceItemProps) => (
    <div className={"flex flex-row lg:flex-col items-center lg:items-start gap-2 lg:gap-0.5"}>
        <div className={"flex items-center gap-1.5"}>
            {icon && <img src={icon} alt={label} className="size-[20px] lg:size-[24px]" />}
            <span className={"text-xs lg:text-sm font-medium text-black tracking-tight"}>{label}</span>
        </div>
        <span className={"text-base lg:text-lg font-semibold text-black tabular-nums"}>{currencyNumberFormat(value)}</span>
    </div>
);

const Separator = ({icon}: {icon: string}) => <span className={"hidden lg:block text-base font-bold text-[#D9D9D9] mx-1"}>{icon}</span>;

export default BookingPackagesViewFooterPrices;
